import { Check } from 'lucide-react-native';
import { type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import type { DecisionOption } from '../../model/types';
import { REPLY_DRAFT_COPY } from '../copy';

import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import { VStack } from '@/components/ui/vstack';
import { getAccessibilityProps } from '@/util/accessibilityProps';

type DecisionCardProps = {
  options: DecisionOption[];
  isDisabled: boolean;
  onSelect: (optionKey: string) => void;
  /** The option the draft was written from, once the expert has chosen one and come back. */
  currentKey?: string | null;
  /** A last card in the same stack, for the expert's own answer. */
  trailing?: ReactNode;
};

/** The classes every card shares, so an own-answer card matches the options. */
export const DECISION_OPTION_CLASS =
  'min-h-14 justify-center rounded-xl border border-outline-500 bg-background-0 px-4 py-3 web:cursor-pointer data-[hover=true]:bg-background-50 data-[active=true]:bg-background-100 data-[disabled=true]:opacity-60';

/**
 * The guidance the expert can start the draft from, as a stack of cards, one
 * per option, each with the same filled secondary "Select" button.
 *
 * Cards replaced a bordered table: the table read as data to scan rather than
 * answers to pick. A card per option gives each answer its own target, spaced
 * by the minimum gap so a tap never lands on a neighbour. The whole card is
 * the target; the button inside is drawn, not pressed, and hidden from screen
 * readers, which already hear each card as a button.
 *
 * The stack is neutral by design: the options keep the server's order and
 * every one is drawn, labeled and announced the same way, with no default
 * focus and no mark on any of them. Every card carries the same secondary
 * button, so it says "pick one" without favoring any, and the brand green stays
 * with the footer's one primary action. A draft shown or
 * nudged before the expert decides anchors their judgment, which is the
 * failure this whole flow is shaped to avoid. The border is `outline-500`, the
 * lightest stop that reads as a control boundary on the card in light mode.
 * The button is `typography-0` on `secondary-600`, which holds AA in both
 * themes.
 *
 * Only after the expert has chosen, and come back to reconsider, is one
 * option marked: theirs, by a tint, a check and the word "Current", so it does
 * not rest on color. Marking it then cannot anchor a decision already made.
 */
export function DecisionCard({
  options,
  isDisabled,
  onSelect,
  currentKey = null,
  trailing,
}: DecisionCardProps) {
  if (options.length === 0 && !trailing) {
    return null;
  }

  return (
    <VStack className="gap-3" testID="reply-draft-options">
      {options.map((option) => {
        const isCurrent = option.key === currentKey;

        return (
          <Pressable
            key={option.key}
            onPress={() => onSelect(option.key)}
            disabled={isDisabled}
            testID={`reply-draft-option-${option.key}`}
            className={twMerge(DECISION_OPTION_CLASS, isCurrent && 'bg-background-50')}
            {...getAccessibilityProps({
              role: 'button',
              label: isCurrent ? REPLY_DRAFT_COPY.currentPositionLabel(option.label) : option.label,
              hint: isCurrent ? REPLY_DRAFT_COPY.keepPositionHint : REPLY_DRAFT_COPY.optionHint,
            })}
            accessibilityState={{ selected: isCurrent, disabled: isDisabled }}>
            <HStack className="items-center gap-3">
              <Text size="md" className="flex-1 font-medium text-typography-900">
                {option.label}
              </Text>
              {isCurrent ? (
                <HStack className="items-center gap-1">
                  <Icon as={Check} size="xs" className="text-primary-700" />
                  <Text size="xs" className="font-semibold text-typography-700">
                    {REPLY_DRAFT_COPY.currentPosition}
                  </Text>
                </HStack>
              ) : (
                <View
                  className="rounded-full bg-secondary-600 px-4 py-1.5"
                  aria-hidden
                  accessibilityElementsHidden
                  importantForAccessibility="no-hide-descendants">
                  <Text size="sm" className="font-semibold text-typography-0">
                    {REPLY_DRAFT_COPY.selectOption}
                  </Text>
                </View>
              )}
            </HStack>
          </Pressable>
        );
      })}

      {trailing}
    </VStack>
  );
}
